import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form, Input, Input as AntdInput } from 'antd';

import { Icon as CroplerIcon, Button } from '@cropler/ui-kit';

import styles from '../../styles/auth.module.scss';

import { AuthTitle } from '../../components';
import { useLoginMutation } from '../../store/api/auth';
import { useViewport } from '../../hooks';
import { BreakpointMobile, BreakpointMobileUp } from '../../constants/ui';
import { ApiFieldError } from '../../types';

const SignIn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { widthViewport } = useViewport();

  const [form] = Form.useForm();

  const [loginUser, { isLoading, isSuccess, isError, error }] = useLoginMutation();

  useEffect(() => {
    if (isSuccess) {
      navigate('/');
    }
  }, [isSuccess, navigate, form, dispatch]);

  useEffect(() => {
    if (isError) {
      const username = form.getFieldValue('loginEmail');
      const [{ detail }] = error as ApiFieldError[];

      form.setFields([{ name: 'loginEmail', value: username, errors: [detail || t('common.smthWrong')] }]);
    }
  }, [isError, error, form, t]);

  const onSubmit = () => {
    const username = form.getFieldValue('loginEmail');
    const password = form.getFieldValue('loginPassword');

    loginUser({ username, password });
  };

  return (
    <>
      <div className={styles.formContainer}>
        <div className={styles.form}>
          <div className={styles.formHeader}>
            <AuthTitle title={widthViewport > BreakpointMobile ? t('login.title') : t('login.title2')} />
            {widthViewport < BreakpointMobileUp && <p className={styles.formText}>{t('login.text')}</p>}
          </div>
          <Form form={form} className={styles.formPassword} autoComplete="off">
            <Form.Item
              name="loginEmail"
              rules={[
                {
                  required: true,
                  type: 'email',
                  message: t('forms.validation.enterEmail'),
                },
              ]}
            >
              <Input prefix={<CroplerIcon name="email" />} placeholder="Email" type="email" size={'large'} />
            </Form.Item>
            <Form.Item
              name="loginPassword"
              rules={[
                {
                  required: true,
                  message: t('forms.validation.enterPassword'),
                },
              ]}
            >
              <AntdInput.Password prefix={<CroplerIcon name="lock" />} placeholder={t('labels.password')} size="large" />
            </Form.Item>
          </Form>
          <Button
            type="primary"
            className={styles.buttonSubmit}
            size={'large'}
            onClick={onSubmit}
            loading={isLoading}
          >
            {t('actions.login')}
          </Button>
          <Button
            type="text"
            className={styles.buttonForgotPassword}
            size={'large'}
            onClick={() => navigate('/login/verify')}
          >
            {t('actions.forgotPassword')}
          </Button>
        </div>
      </div>
      <div className={styles.authFooter}>
        <span>{t('login.dontHaveAccount')}</span>
        <Link to="/signup" className={styles.link}>
          {t('actions.signup')}
        </Link>
      </div>
    </>
  );
};

export default SignIn;
